import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Receipt, AlertCircle, FileText, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Configurar worker do PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface ModalFaturaVisualizacaoProps {
  clienteId: string;
  razaoSocial?: string;
  cnpj?: string;
  trigger?: React.ReactNode;
}

export function ModalFaturaVisualizacao({ 
  clienteId, 
  razaoSocial, 
  trigger 
}: ModalFaturaVisualizacaoProps) {
  const [open, setOpen] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // Buscar última fatura do cliente
  const { data, isLoading, error } = useQuery({
    queryKey: ['fatura-visualizacao-modal', clienteId],
    queryFn: async () => {
      // 1. Buscar fatura_upload mais recente (apenas colunas que existem)
      const { data: faturaUpload, error: errUpload } = await supabase
        .from('fatura_upload')
        .select('id, arquivo_url, created_at')
        .eq('cliente_id', clienteId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (errUpload || !faturaUpload) {
        return { arquivoUrl: null, analise: null, hasUpload: false };
      }

      // 2. Buscar análise se fatura foi processada
      let analise = null;
      if (faturaUpload.id) {
        const { data: analiseData } = await supabase
          .from('fatura_analise')
          .select('json_resultado, created_at')
          .eq('fatura_id', faturaUpload.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        analise = (analiseData as any)?.json_resultado || null;
      }

      return { 
        arquivoUrl: faturaUpload.arquivo_url, 
        analise, 
        hasUpload: true 
      };
    },
    enabled: open,
  });

  // Carregar PDF do bucket privado quando tiver arquivo_url
  useEffect(() => {
    let cancelled = false;

    if (!data?.arquivoUrl || !open) {
      setPdfBlob(null);
      setPageNumber(1);
      setNumPages(0);
      setPdfError(null);
      return;
    }

    const loadPdf = async () => {
      try {
        // Normalizar path (remover prefixo se existir)
        const path = data.arquivoUrl!.replace(/^faturas\//, '');
        console.log('[Fatura] Carregando PDF, path:', path);
        
        // Tentar download do bucket privado
        const { data: blob, error: downloadError } = await supabase.storage
          .from('faturas')
          .download(path);

        if (cancelled) return;

        if (downloadError || !blob) {
          console.error('[Fatura] Erro ao baixar PDF:', downloadError);
          setPdfError('Erro ao baixar o arquivo PDF');
          return;
        }

        console.log('[Fatura] Blob recebido - size:', blob.size, 'type:', blob.type);

        // Garantir que o Blob tenha o tipo correto (PDF)
        // Isso evita problemas quando o storage retorna application/octet-stream
        const typedBlob = blob.type === 'application/pdf' 
          ? blob 
          : new Blob([blob], { type: 'application/pdf' });

        if (cancelled) return;

        console.log('[Fatura] Blob tipado criado, tamanho:', typedBlob.size);
        setPdfBlob(typedBlob);
        setPdfError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('[Fatura] Erro ao carregar PDF:', err);
        setPdfError('Erro ao processar o arquivo PDF');
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
    };
  }, [data?.arquivoUrl, open]);

  // Limpar dados ao fechar modal
  useEffect(() => {
    if (!open) {
      setPdfBlob(null);
      setPageNumber(1);
      setNumPages(0);
      setPdfError(null);
      setScale(1.0);
    }
  }, [open]);

  // Callbacks do react-pdf
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    console.log('[Fatura] PDF carregado com', numPages, 'páginas');
    setNumPages(numPages);
    setPageNumber(1);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('[Fatura] Erro ao renderizar PDF:', error);
    setPdfError('Erro ao renderizar o PDF');
  };

  // Navegação de páginas
  const goToPrevPage = () => setPageNumber((prev) => Math.max(prev - 1, 1));
  const goToNextPage = () => setPageNumber((prev) => Math.min(prev + 1, numPages));

  // Zoom
  const zoomIn = () => setScale((prev) => Math.min(prev + 0.2, 2.5));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.5));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button 
            variant="outline" 
            size="sm" 
            className="gap-2 border-info text-info hover:bg-info/5"
          >
            <Receipt className="w-4 h-4" />
            Fatura
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <div className="bg-info text-white p-1.5 rounded">
              <Receipt className="w-5 h-5" />
            </div>
            Visualização da Fatura
          </DialogTitle>
          {razaoSocial && (
            <p className="text-sm text-muted-foreground">{razaoSocial}</p>
          )}
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-[60vh] w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : error || !data?.hasUpload ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground">
                Nenhuma fatura disponível
              </h3>
              <p className="text-sm text-muted-foreground/70 mt-1">
                Faça o upload da fatura na aba "Fatura" para visualizá-la aqui.
              </p>
            </div>
          ) : (
            <>
              {/* Visualização do PDF com react-pdf */}
              {pdfError ? (
                <div className="border rounded-lg p-6 text-center bg-destructive/10">
                  <AlertCircle className="w-8 h-8 mx-auto text-destructive mb-2" />
                  <p className="text-sm text-destructive">{pdfError}</p>
                </div>
              ) : pdfBlob ? (
                <div className="border rounded-lg overflow-hidden">
                  {/* Header com controles */}
                  <div className="bg-muted px-3 py-2 border-b flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Fatura PDF</span>
                    </div>
                    
                    {/* Controles de navegação e zoom */}
                    <div className="flex items-center gap-2">
                      {/* Zoom */}
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7" 
                        onClick={zoomOut}
                        disabled={scale <= 0.5}
                      >
                        <ZoomOut className="w-4 h-4" />
                      </Button>
                      <span className="text-xs text-muted-foreground min-w-[40px] text-center">
                        {Math.round(scale * 100)}%
                      </span>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7" 
                        onClick={zoomIn}
                        disabled={scale >= 2.5}
                      >
                        <ZoomIn className="w-4 h-4" />
                      </Button>

                      <div className="w-px h-4 bg-border mx-1" />

                      {/* Paginação */}
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7" 
                        onClick={goToPrevPage}
                        disabled={pageNumber <= 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-xs text-muted-foreground min-w-[60px] text-center">
                        {pageNumber} / {numPages || '?'}
                      </span>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7" 
                        onClick={goToNextPage}
                        disabled={pageNumber >= numPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Área do PDF */}
                  <div className="h-[75vh] overflow-auto bg-muted/30 flex justify-center p-4">
                    <Document
                      file={pdfBlob}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={onDocumentLoadError}
                      loading={
                        <div className="flex items-center justify-center h-full">
                          <Skeleton className="h-[50vh] w-[400px]" />
                        </div>
                      }
                      error={
                        <div className="flex flex-col items-center justify-center h-full">
                          <AlertCircle className="w-8 h-8 text-destructive mb-2" />
                          <p className="text-sm text-destructive">Erro ao carregar o PDF</p>
                        </div>
                      }
                    >
                      <Page 
                        pageNumber={pageNumber} 
                        scale={scale}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        loading={
                          <Skeleton className="h-[50vh] w-[400px]" />
                        }
                      />
                    </Document>
                  </div>
                </div>
              ) : data?.arquivoUrl ? (
                <div className="border rounded-lg p-6 text-center bg-muted/30">
                  <Skeleton className="h-[40vh] w-full" />
                  <p className="text-sm text-muted-foreground mt-2">Carregando PDF...</p>
                </div>
              ) : (
                <div className="border rounded-lg p-6 text-center bg-muted/30">
                  <FileText className="w-8 h-8 mx-auto text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground">PDF não disponível</p>
                </div>
              )}

            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
